import { makeAutoObservable, runInAction } from 'mobx';
import { authService } from '../services/authService';
import { LoginCredentials, User, Organization } from '../types/Auth';

export class AuthStore {
  user: User | null = null;
  organizations: Organization[] = [];
  selectedOrganization: Organization | null = null;
  isLoading = false;
  error: string | null = null;
  isAuthenticated = false;

  constructor() {
    makeAutoObservable(this);
  }

  setError(error: string | null) {
    this.error = error;
  }

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  async login(credentials: LoginCredentials) {
    this.setLoading(true);
    this.setError(null);

    try {
      // Step 1: Login to get token
      const loginResponse = await authService.login(credentials);

      // Store token first so subsequent API calls can use it
      localStorage.setItem('token', loginResponse.token);

      // Step 2: Get current user details
      const user = await authService.getCurrentUser();

      // Step 3: Set user's organization_id for subsequent API calls
      localStorage.setItem('organization_id', user.organization_id.toString());

      // Step 4: Get organizations list (now with Organization-ID header)
      const organizations = await authService.getOrganizations();

      runInAction(() => {
        this.user = user;
        this.organizations = organizations;
        this.isAuthenticated = true;
      });

      // Store user data and organizations
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('organizations', JSON.stringify(organizations));
      
      // Auto-select first organization if none is selected
      if (organizations.length > 0) {
        // Use user's current organization_id from the user object, or first org
        const currentOrg = organizations.find(org => org.id === user.organization_id) || organizations[0];
        await this.selectOrganization(currentOrg);
      }

      return { success: true };
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message;
        this.isAuthenticated = false;
      });
      return { success: false, error: error.message };
    } finally {
      this.setLoading(false);
    }
  }

  async logout() {
    this.setLoading(true);

    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state and storage
      runInAction(() => {
        this.user = null;
        this.organizations = [];
        this.selectedOrganization = null;
        this.isAuthenticated = false;
        this.error = null;
      });

      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('organizations');
      localStorage.removeItem('organization_id');
      this.setLoading(false);
    }
  }

  async selectOrganization(organization: Organization) {
    runInAction(() => {
      this.selectedOrganization = organization;
    });

    // Store organization ID for API headers
    localStorage.setItem('organization_id', organization.id.toString());
  }

  checkAuthStatus() {
    try {
      const token = localStorage.getItem('token');
      const userString = localStorage.getItem('user');
      const organizationsString = localStorage.getItem('organizations');
      const organizationId = localStorage.getItem('organization_id');

      console.log('token', token)


      if (token && userString) {
        const user = JSON.parse(userString);
        const organizations = organizationsString ? JSON.parse(organizationsString) : [];
        
        runInAction(() => {
          this.user = user;
          this.organizations = organizations;
          this.isAuthenticated = true;
        });

        // TODO: Validate token with server
        // const validation = await authService.validateToken();
        // if (!validation.valid) {
        //   await this.logout();
        //   return;
        // }

        // If we have a stored organization ID, set it as selected
        if (organizationId && this.organizations.length > 0) {
          const org = this.organizations.find(o => o.id.toString() === organizationId);
          if (org) {
            this.selectedOrganization = org;
          }
        }
      }
    } catch (error) {
      console.error('Auth status check error:', error);
      // await this.logout();
    }
  }
}